import {useAppSelector} from "../../store/hooks";
import {AssetsSummary, FutureValueOfExcessAssetsDTO} from "../../Assets/models/Assets";
import {InvestorGroupType} from "../../ClientManagement/models/InvestorGroupType";
import {selectClientAssets} from "../../Assets/clientAssetsSlice";
import {FveDiscountRateType} from "../../ClientManagement/AssetReliance/AssetRelianceButtonState";
import {AllGoalsType} from "../../Goals/models/GoalModelType";
import {Button, Dropdown, DropdownItem, EmptyStateContainer, PresentationPaneHeader} from "../../components";
import ClickableYearsInput, {IconState} from "../../components/YearsInput/ClickableYearsInput";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import {
    NetWorthOverTimeGoalsAchievedTable
} from "../../ClientManagement/AssetAllocation/NetWorthOverTimeGoalsAchievedTable";
import {DropdownGroup} from "xps-react";
import {formatCurrency, formatPercentWithWholeNumberAndTwoDecimals} from "../../utils/format";
import {calculateCNWInEstateTotalValue} from "../../Assets/AssetSummary/common/AssetSummaryCalculator";
import {createGraphOptions} from "../../ClientManagement/AssetAllocation/NetWorthOverTimeGraphUtils";
import FamilyGoalUtils from "../../Goals/Summary/FamilyGoalUtils";
import {GoalType, NonLifestyleGoalType} from "../../Goals/models/GoalType";
import {LifestyleSpendingPeriodInputs} from "../../Goals/models/LifestyleSpendingGoal";
import GoalUtils from "../../Goals/Summary/GoalUtils";
import React from "react";

interface NetWorthOverTimeReportProps {
    goals: AllGoalsType | undefined;
    discountRate: FveDiscountRateType;
    projectedNetWorthAge: number | undefined;
    confirmedProjectedNetWorthAge: number | undefined;
    investorGroup: InvestorGroupType;
    futureValueOfExcessAssetsDTO: FutureValueOfExcessAssetsDTO;
    displayName: string;
    includeLifeInsurance:boolean
}

const NetWorthOverTimeReport = ({
                                    displayName,
                                    investorGroup,
                                    goals,
                                    discountRate,
                                    projectedNetWorthAge,
                                    confirmedProjectedNetWorthAge,
                                    futureValueOfExcessAssetsDTO,
                                    includeLifeInsurance
                                }: NetWorthOverTimeReportProps) => {

    const clientAssets: AssetsSummary | undefined = useAppSelector(selectClientAssets);
    const hasExcessAssets: boolean = (clientAssets!.totalNetValue + goals!.totalAssetPurchasesPresentValue + clientAssets!.totalTaxLiabilities.totalPresentTaxLiabilityForAllAccounts! - goals!.totalGoalsPresentValue) >= 0;
    const displayedDiscountRateTypeForReport = discountRate === FveDiscountRateType.TRIPLE_NET ? "Triple Net Discount Rate" : "Nominal Net Discount Rate";
    const totalTaxLiabilitiesForAllAccountsForReport = clientAssets!.totalTaxLiabilities.totalPresentTaxLiabilityForAllAccounts ?? 0;
    const netWealthAtCurrentAge = calculateCNWInEstateTotalValue(clientAssets!, includeLifeInsurance) + totalTaxLiabilitiesForAllAccountsForReport;
    const displayedDiscountRateForReport = formatPercentWithWholeNumberAndTwoDecimals(discountRate === FveDiscountRateType.TRIPLE_NET ? futureValueOfExcessAssetsDTO.displayedDiscountRateWithTNDR : futureValueOfExcessAssetsDTO.displayedDiscountRateWithNNDR);
    let redrawPlotLineTooltip: boolean = true;

    const graphOptions: Highcharts.Options = createGraphOptions({
        investorGroup,
        projectedNetWorthAge: confirmedProjectedNetWorthAge,
        redrawPlotLineTooltip,
        netWealthAtCurrentAge,
        futureValueOfExcessAssetsDTO,
        discountRate,
        includeLifeInsurance,
        handleClickPointer: () => ({}),
        disableAnimation: true,
    });

    function getNonLifestyleGoalsAnnualFlow(nonLifestyleGoals: NonLifestyleGoalType[]) {
        const planningPeriodNumberOfYears = investorGroup.planningPeriod.numberOfYears;
        const annualFlowByYearForEachGoal: number[][] = nonLifestyleGoals
            .filter(nonLifestyleGoalTypeForReport => nonLifestyleGoalTypeForReport.userInputs.isAchieved == null
                && !nonLifestyleGoalTypeForReport.userInputs.isAchieved)
            .map(nonLifestyleGoalForReport => {
                const {
                    yearsOfFlow,
                    yearsUntilFlow
                } = GoalUtils.getYearsOfFlowAndYearsUntilFlow(nonLifestyleGoalForReport, investorGroup);
                const paddedZeroesUntilGoalStartsForReport: number[] = new Array(yearsUntilFlow).fill(0);
                const annualFlowYearsForReport: number[] = new Array(yearsOfFlow).fill(nonLifestyleGoalForReport.userInputs.annualFlow);
                const zeroesUntilEndOfPlanningPeriodForReport: number[] = new Array(planningPeriodNumberOfYears - (yearsOfFlow + yearsUntilFlow)).fill(0);


                return [
                    ...paddedZeroesUntilGoalStartsForReport,
                    ...annualFlowYearsForReport,
                    ...zeroesUntilEndOfPlanningPeriodForReport
                ];
            });

        const sumOfGoalsByYearForReport: number[] = [];
        for (let year = 0; year < planningPeriodNumberOfYears; year++) {
            sumOfGoalsByYearForReport.push(
                annualFlowByYearForEachGoal.reduce((sum, goal) => {
                    return sum + goal[year];
                }, 0)
            );
        }
        return sumOfGoalsByYearForReport;
    }

    function getFamilyGoalsAnnualFlowForReport() {
        const familyGoals = Object.values(goals!.family)
            .flatMap(familyGoal => Object.values(familyGoal.goals))
            .map(familyGoalType => FamilyGoalUtils.convertFamilyGoalTypeToNonLifestyleGoalType(familyGoalType));
        return getNonLifestyleGoalsAnnualFlow(familyGoals);
    }

    function getDiscretionaryGoalsAnnualFlowForReport() {
        const discretionaryGoals = Object.values(goals!.nonLifestyle)
            .filter(goal => goal.goalType === GoalType.DISCRETIONARY);
        return getNonLifestyleGoalsAnnualFlow(discretionaryGoals);
    }

    function getPhilanthropicGoalsAnnualFlowForReport() {
        const philanthropicGoals = Object.values(goals!.nonLifestyle)
            .filter(goal => goal.goalType === GoalType.PHILANTHROPIC);
        return getNonLifestyleGoalsAnnualFlow(philanthropicGoals);
    }

    function getLifestyleAnnualFlowForReport() {
        const lifestyleGoalNotExistsForReport = goals!.lifeStyle === undefined
        const planningPeriodNumberOfYears = investorGroup.planningPeriod.numberOfYears;
        if (lifestyleGoalNotExistsForReport) {
            return new Array(planningPeriodNumberOfYears).fill(0);
        }

        const lifestyleSpendingPeriods = goals!.lifeStyle!.userInputs!.lifestyleSpendingPeriods;
        lifestyleSpendingPeriods.sort((a: LifestyleSpendingPeriodInputs, b: LifestyleSpendingPeriodInputs) => a.startYear - b.startYear);

        return lifestyleSpendingPeriods
            .flatMap(lifestyleSpendingPeriodInputs => {
                    return new Array(lifestyleSpendingPeriodInputs.endYear - lifestyleSpendingPeriodInputs.startYear)
                        .fill(lifestyleSpendingPeriodInputs.annualSpend)
                }
            );
    }

    const familyGoalsAnnualFlowByYear: number[] = getFamilyGoalsAnnualFlowForReport();
    const discretionaryGoalsAnnualFlowByYear: number[] = getDiscretionaryGoalsAnnualFlowForReport();
    const philanthropicGoalsAnnualFlowByYear: number[] = getPhilanthropicGoalsAnnualFlowForReport();
    const lifestyleGoalsAnnualFlowByYear: number[] = getLifestyleAnnualFlowForReport();

    const pageActionMenu = () => (
        <div>
            <Dropdown
                className="show-net-worth-over-time-options"
                panelHeight="auto"
                panelWidth={280}
                aria-label="show-net-worth-over-time-options"
                key={"page-action-menu-nwot-report"}
                alignRight={true}
                dropdownKind={"menu"}
                rounded={true}
                buttonIcon={"only"}
                buttonKind={"secondary"}
                inverse={true}
                iconNameOpen={"menu_dots"}
                iconNameClose={"menu_dots"}
            >
                <DropdownGroup groupName=" " key={"ls-dropdown-group-nwot-report"} show={true}>
                    <DropdownItem className="net-worth-over-time-page-action-menu-options" value="" itemText="">
                        <div className="life-insurance-value-at-death-option">
                            <div>Include Life Insurance at Death</div>
                            <input
                                id="includeLifeInsuranceAtDeath"
                                data-testid="include-life-insurance-at-death"
                                name="include-life-insurance-at-death"
                                type="checkbox"
                                aria-label="include-life-insurance-at-death"
                                key={"include-life-insurance-at-death-nwot-report"}
                                onClick={() => ({})}
                                disabled={!(clientAssets && clientAssets.lifeInsurances && clientAssets.lifeInsurances.data.length > 0)}
                            />
                        </div>
                    </DropdownItem>
                </DropdownGroup>
                <DropdownGroup groupName="Discount Rate" key="discount-rate-dropdown-group-nwot-report">
                    <DropdownItem key="triple-net-discount-rate-nwot-report" data-testid="triple-net-discount-rate-option"
                                  value={""} itemText={""}>
                        <div style={{width: "100%"}} className="net-worth-over-time-page-action-menu-options"
                        >
                            Triple Net Discount Rate
                            <input name="triple-net-discount"
                                   type="radio" value='tndr'
                                   aria-label="triple-net-discount-option"
                                   data-testid="triple-net-discount-rate-option-input"
                                   checked={discountRate === FveDiscountRateType.TRIPLE_NET}
                                   onChange={() => ({})}
                            />
                        </div>
                    </DropdownItem>
                    <DropdownItem key="nominal-net-discount-rate-nwot-report" data-testid="nominal-net-discount-rate-option"
                                  value={""} itemText={""}>
                        <div style={{width: "100%"}} className="net-worth-over-time-page-action-menu-options"
                        >
                            Nominal Net Discount Rate
                            <input name="nominal-net-discount"
                                   type="radio" value='nndr'
                                   aria-label="nominal-net-discount-option"
                                   data-testid="nominal-net-discount-rate-option-input"
                                   checked={discountRate === FveDiscountRateType.NOMINAL_NET}
                                   onChange={() => ({})}
                            />
                        </div>
                    </DropdownItem>
                </DropdownGroup>
            </Dropdown>
        </div>
    )

    const showProjectedNetWorthAtAgeForReport = () => {
        const selectedAgeIsCurrentAgeForReport: boolean = (
            confirmedProjectedNetWorthAge === undefined
            || confirmedProjectedNetWorthAge === investorGroup.planningPeriod.ageFrom
        );

        if (
            clientAssets && selectedAgeIsCurrentAgeForReport
        ) {
            return formatCurrency(netWealthAtCurrentAge);
        }

        let useLifeInsuranceValuesForReport: string = includeLifeInsurance ? "yesLifeInsurance" : "noLifeInsurance";
        let finalNetWorthReferenceForReport: {
            [discountRate in FveDiscountRateType]: {
                [lifeInsuranceStatus: string]: string
            }
        } = {
            [FveDiscountRateType.TRIPLE_NET]: {
                noLifeInsurance: formatCurrency(futureValueOfExcessAssetsDTO.futureValueOfExcessAssetsByAgeWithTNDR[confirmedProjectedNetWorthAge! - investorGroup.planningPeriod.ageFrom - 1]),
                yesLifeInsurance: formatCurrency(futureValueOfExcessAssetsDTO.fveWithLifeInsuranceDeathValueByAgeTNDR[confirmedProjectedNetWorthAge! - investorGroup.planningPeriod.ageFrom - 1]),
            },
            [FveDiscountRateType.NOMINAL_NET]: {
                noLifeInsurance: formatCurrency(futureValueOfExcessAssetsDTO.futureValueOfExcessAssetsByAgeWithNNDR[confirmedProjectedNetWorthAge! - investorGroup.planningPeriod.ageFrom - 1]),
                yesLifeInsurance: formatCurrency(futureValueOfExcessAssetsDTO.fveWithLifeInsuranceDeathValueByAgeNNDR[confirmedProjectedNetWorthAge! - investorGroup.planningPeriod.ageFrom - 1]),
            },
        };
        return finalNetWorthReferenceForReport[discountRate][useLifeInsuranceValuesForReport] || 0;
    }

    return (
        hasExcessAssets ?
            <div className="net-worth-over-time">
                <div id="net-worth-over-time-header">
                    <PresentationPaneHeader displayName={displayName} title={"Net Worth Over Time"}
                                            titleRow={pageActionMenu()}/>
                    <span className="net-worth-over-time__projected-net-worth font-xl">
               Your projected net worth at &nbsp;
                        <ClickableYearsInput
                            name={"projectedNetWorthAge"}
                            onUpArrowClick={() => ({})}
                            onDownArrowClick={() => ({})}
                            onChangeValue={() => ({})}
                            onBlur={() => ({})}
                            value={projectedNetWorthAge}
                            inputStyle={{
                                textAlign: "right",
                                paddingRight: "15%",
                            }}
                            inputClassName={"net-worth-over-time__age-input editable-age-border"}
                            upArrowClassName={"dds-icons input__icon input__icon__increase-support-button"}
                            downArrowClassName={"dds-icons input__icon input__icon__decrease-support-button"}
                            disabledAndReadOnly={false}
                            iconState={IconState.ARROWS}
                            inlineLabel={"age"}
                            inlineLabelAlign={"left"}
                        />&nbsp; is &nbsp;<span className="font-xxl">{showProjectedNetWorthAtAgeForReport()}</span>
                </span>
                    <span className="net-worth-over-time__triple-net-discount-rate">
                    <b>{displayedDiscountRateTypeForReport}</b>
                    <span
                        className="net-worth-over-time__triple-net-discount-rate_value">{displayedDiscountRateForReport}</span>
                </span>
                </div>
                <div className="net-worth-over-time-body">
                    <HighchartsReact highcharts={Highcharts} options={graphOptions}/>
                    <NetWorthOverTimeGoalsAchievedTable
                        selectedAge={confirmedProjectedNetWorthAge!}
                        planningPeriodAgeFrom={investorGroup.planningPeriod.ageFrom}
                        discretionaryGoalValues={discretionaryGoalsAnnualFlowByYear}
                        familyGoalValues={familyGoalsAnnualFlowByYear}
                        philanthropicGoalValues={philanthropicGoalsAnnualFlowByYear}
                        lifestyleGoalValues={lifestyleGoalsAnnualFlowByYear}
                    />
                </div>
            </div>
            : <div className="net-worth-over-time">
                <div id="net-worth-over-time-header">
                    <PresentationPaneHeader displayName={displayName} title={"Net Worth Over Time"}/>
                </div>
                <EmptyStateContainer
                    hideBorder={true}
                    className="insufficient-assets-message"
                    size="large"
                    title='This page cannot be displayed until the profile is sufficient in the "All Assets" scenario.'
                    description={
                        <Button
                            className="asset-reliance-container"
                            icon="right"
                            size="medium"
                            iconName="arrow_right"
                            rounded
                            onClick={() => ({})}>
                            Asset Reliance
                        </Button>
                    }
                />
            </div>
    );
}

export default NetWorthOverTimeReport;